import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type { StoreManagementState } from '../types';
import type {
    StoreBrandSearchResult,
    StoreSearchResult,
    StoreLiveStatus,
} from '../../types/api.types';
import {
    getStoresByBrand,
    updateStoreLiveStatus as updateStoreLiveStatusApi,
} from '../../services/storeManagementService';

const initialState: StoreManagementState = {
    selectedStoreBrand: null,
    stores: [],
    loading: 'idle',
    error: null,
    updatingStoreIds: [],
};

// ============================================
// Async Thunks
// ============================================

export const fetchStores = createAsyncThunk(
    'storeManagement/fetchStores',
    async (storeBrandId: string) => {
        const results = await getStoresByBrand(storeBrandId);
        return results;
    }
);

export const updateStoreLiveStatus = createAsyncThunk(
    'storeManagement/updateLiveStatus',
    async (
        {
            storeId,
            storeStatus,
            storeBrandId,
        }: { storeId: string; storeStatus: StoreLiveStatus; storeBrandId: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await updateStoreLiveStatusApi(storeId, storeStatus, storeBrandId);
            if (response.status !== 'success') {
                return rejectWithValue({ message: response.message || 'Failed to update status' });
            }
            return response;
        } catch (error: unknown) {
            const errorMessage =
                (error as { message?: string })?.message ||
                (error as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message ||
                'Failed to update store live status';
            return rejectWithValue({ message: errorMessage });
        }
    }
);

// ============================================
// Slice
// ============================================

const storeManagementSlice = createSlice({
    name: 'storeManagement',
    initialState,
    reducers: {
        setSelectedStoreBrand: (
            state,
            action: { payload: StoreBrandSearchResult | null; type: string }
        ) => {
            state.selectedStoreBrand = action.payload;
            state.stores = [];
            state.error = null;
        },

        clearStoreManagementError: (state) => {
            state.error = null;
        },

        resetStoreManagementState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStores.pending, (state) => {
                state.loading = 'loading';
                state.error = null;
            })
            .addCase(fetchStores.fulfilled, (state, action) => {
                state.loading = 'success';
                state.stores = action.payload;
                state.error = null;
            })
            .addCase(fetchStores.rejected, (state, action) => {
                state.loading = 'error';
                state.error = action.error.message || 'Failed to fetch stores';
            })

            .addCase(updateStoreLiveStatus.pending, (state, action) => {
                state.updatingStoreIds.push(action.meta.arg.storeId);
            })
            .addCase(updateStoreLiveStatus.fulfilled, (state, action) => {
                const { _id, storeStatus } = action.payload.data;
                state.updatingStoreIds = state.updatingStoreIds.filter((id) => id !== _id);

                const storeIndex = state.stores.findIndex(
                    (s: StoreSearchResult) => s._id === _id
                );
                if (storeIndex !== -1) {
                    state.stores[storeIndex].storeStatus = storeStatus;
                }
                state.error = null;
            })
            .addCase(updateStoreLiveStatus.rejected, (state, action) => {
                const storeId = action.meta.arg.storeId;
                state.updatingStoreIds = state.updatingStoreIds.filter((id) => id !== storeId);
                const errorPayload = action.payload as { message?: string } | undefined;
                state.error =
                    errorPayload?.message ||
                    action.error.message ||
                    'Failed to update store live status';
            });
    },
});

// ============================================
// Actions
// ============================================

export const { setSelectedStoreBrand, clearStoreManagementError, resetStoreManagementState } =
    storeManagementSlice.actions;

// ============================================
// Selectors (Custom Hooks)
// ============================================

export const useStoreManagementStores = () => {
    return useSelector((state: RootState) => state.storeManagement.stores);
};

export const useStoreManagementSelectedBrand = () => {
    return useSelector((state: RootState) => state.storeManagement.selectedStoreBrand);
};

export const useStoreManagementLoading = () => {
    return useSelector((state: RootState) => state.storeManagement.loading);
};

export const useStoreManagementError = () => {
    return useSelector((state: RootState) => state.storeManagement.error);
};

export const useStoreManagementUpdatingIds = () => {
    return useSelector((state: RootState) => state.storeManagement.updatingStoreIds);
};

// ============================================
// Reducer
// ============================================

export default storeManagementSlice.reducer;
